import { format, parseISO } from 'date-fns';
import { sv } from 'date-fns/locale';
import {
    styled,
    SwipeableDrawer,
    Typography,
    useMediaQuery,
} from '@mui/material';
import { Button } from 'components/Button/Button';
import { IconButton } from 'components/IconButton/IconButton';
import { EntryFields } from '../shared/EntryFields';
import { EntryEditorProps } from '../types';

const BottomDrawer = styled(SwipeableDrawer)(({ theme }) => ({
    '& .MuiDrawer-paper': {
        height: 'auto',
        maxHeight: 'min(90dvh, calc(100dvh - env(safe-area-inset-top, 0px)))',
        borderTopLeftRadius: theme.shape.borderRadius * 2,
        borderTopRightRadius: theme.shape.borderRadius * 2,
        overflow: 'hidden',
        backgroundColor: theme.palette.background.paper,
    },
}));

const DragHandle = styled('div')(({ theme }) => ({
    'display': 'flex',
    'justifyContent': 'center',
    'flexShrink': 0,
    'padding': theme.spacing(1.5, 0, 0.5),
    '&::after': {
        content: "''",
        width: 36,
        height: 4,
        borderRadius: 2,
        backgroundColor: theme.palette.grey[400],
    },
}));

const DrawerHeader = styled('div')(({ theme }) => ({
    flexShrink: 0,
    userSelect: 'none',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
}));

const DrawerActions = styled('div')(({ theme }) => ({
    flexShrink: 0,
    marginTop: 'auto',
    padding: theme.spacing(2),
    paddingBottom: `calc(${theme.spacing(2)} + env(safe-area-inset-bottom, 0px))`,
}));

interface Props extends EntryEditorProps {
    open: boolean;
    onExited?: () => void;
}

export function EntryDrawer({ open, onExited, ...editorProps }: Props) {
    const reduceMotion = useMediaQuery('(prefers-reduced-motion: reduce)');

    return (
        <BottomDrawer
            anchor='bottom'
            open={open}
            onClose={editorProps.onClose}
            // Rows open the editor; swiping only dismisses it.
            onOpen={() => {}}
            disableSwipeToOpen
            disableDiscovery
            hysteresis={0.3}
            transitionDuration={reduceMotion ? 0 : { enter: 400, exit: 280 }}
            SlideProps={{
                // Each selected row mounts a drawer, so its first open should animate too.
                appear: true,
                onExited,
                easing: {
                    enter: 'cubic-bezier(0.22, 1, 0.36, 1)',
                    exit: 'cubic-bezier(0.4, 0, 1, 1)',
                },
            }}
            ModalProps={{
                // Our pickers portal their menus to body, outside the drawer's focus trap.
                disableEnforceFocus: true,
            }}>
            <DragHandle />
            <DrawerHeader>
                <Typography
                    variant='body1'
                    fontWeight={700}
                    noWrap
                    sx={{ '&::first-letter': { textTransform: 'uppercase' } }}>
                    {format(
                        parseISO(editorProps.draft.reportingDate),
                        'EEEE d MMMM',
                        {
                            locale: sv,
                        }
                    )}
                </Typography>
                {!editorProps.readOnly && (
                    <IconButton
                        variant='text'
                        icon='delete'
                        sx={{ color: 'error.main', flexShrink: 0 }}
                        disabled={editorProps.disabled}
                        onClick={editorProps.onDelete}
                    />
                )}
            </DrawerHeader>
            <EntryFields {...editorProps} />
            {!editorProps.readOnly && (
                <DrawerActions>
                    <Button
                        variant='secondary'
                        fullWidth
                        onClick={editorProps.onClose}>
                        Spara
                    </Button>
                </DrawerActions>
            )}
        </BottomDrawer>
    );
}
